'use client';
import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { Message } from '@/types';
import { cn } from '@/lib/utils';
import SourceCard from './SourceCard';
import MarkdownRenderer from './MessageBubble/MarkdownRenderer';
import ThinkingSection from './MessageBubble/ThinkingSection';
import MessageActions from './MessageBubble/MessageActions';
import FilePreview from './FilePreview';
import { useMessageActions } from '@/hooks/chat/useMessageActions';
import { useTranslation } from 'react-i18next';
import { useThinkingCollapse } from '@/hooks/chat/useThinkingCollapse';
// import { Check, X, Clock, Loader2, Square, Reply } from 'lucide-react';
import { Check, X, Reply, Loader2 } from 'lucide-react';
import { apiUpload } from '@/lib/api';
// import { Check, X, Square, Reply } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { submitMessageFeedback } from '@/services/conversationService';
import { toast } from '@/hooks/ui/use-toast';
import { showToast } from '@/components/Toast';
import ImageLightbox, { LightboxImage } from '@/components/ImageLightbox';

interface MessageBubbleProps {
  message: Message;
  onSourceClick: (sourceId: string, page?: number) => void;
  onRetry?: () => void;
  onDelete?: () => void;
  onChangeChoice?: (messageId: string, newActiveChoice: number) => void;
  onEditSave?: (
    message: Message,
    newContent: string,
    fileIds?: string[]
  ) => void;
  selectedAgent?: {
    id: string;
    name: string;
    logo?: string;
  };
  conversationId?: string;
  // 只读模式：隐藏操作按钮（copy/retry/delete/edit/feedback），但保留时间和版本切换器
  readOnly?: boolean;
  // 是否有任何消息正在流式传输（用于禁用所有消息的操作按钮）
  isAnyStreaming?: boolean;
  // 🔥 新增：取消消息的回调（用于 waiting 和 ongoing 状态）
  onCancel?: () => void;
  // 🔥 群聊相关
  isGroupChat?: boolean;
  onReply?: (message: Message) => void; // 回复消息回调
}
export default function MessageBubble({
  message,
  onSourceClick,
  onRetry,
  onDelete,
  onChangeChoice,
  onEditSave,
  selectedAgent: _selectedAgent,
  conversationId,
  readOnly = false,
  isAnyStreaming = false,
  onCancel,
  isGroupChat = false,
  onReply,
}: MessageBubbleProps) {
  const { t } = useTranslation();
  const isUser = message.role === 'user';
  // 🔥 群聊：区分自己的消息和其他用户的消息
  const isOwnMessage = (message as any).isOwnMessage !== false; // 默认 true（单聊场景）
  const otherUser = (message as any).user; // 群聊中其他用户的信息
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  // 🔥 附件编辑相关状态
  const [editedFileIds, setEditedFileIds] = useState<string[]>([]); // 保留的原有附件 ID
  // 🔥 修改：新添加的文件立即上传，存储上传状态
  interface NewFileUpload {
    id: string; // 临时 ID
    file: File;
    previewUrl: string;
    fileId?: string; // 上传成功后填充
    isUploading: boolean;
    error?: string;
  }
  const [newFiles, setNewFiles] = useState<NewFileUpload[]>([]); // 新添加的附件（立即上传）
  const fileInputRef = useRef<HTMLInputElement>(null); // 文件上传输入框引用

  // 🔥 文件上传函数：选择文件时立即上传
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const uploadNewFile = async (file: File) => {
    const tempId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const previewUrl = URL.createObjectURL(file);

    const fileEntry: NewFileUpload = {
      id: tempId,
      file,
      previewUrl,
      isUploading: true,
    };

    setNewFiles(prev => [...prev, fileEntry]);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiUpload<{
        fileId: string;
        code?: number;
        message?: string;
        msg?: string;
      }>('/api/messages/files', formData, undefined, {
        skipErrorToast: true,
      });

      // 🔥 检查 response.code === 200 来判断成功
      if (response?.code === 200 && response?.fileId) {
        setNewFiles(prev =>
          prev.map(f =>
            f.id === tempId
              ? { ...f, fileId: response.fileId, isUploading: false }
              : f
          )
        );
      } else {
        // 🔥 显示后端返回的错误信息
        const errorMsg = response?.message || response?.msg || 'Upload failed';
        showToast(errorMsg, 'error', { title: 'Error' });
        setNewFiles(prev =>
          prev.map(f =>
            f.id === tempId ? { ...f, isUploading: false, error: errorMsg } : f
          )
        );
      }
    } catch (error) {
      const errorMsg = 'Upload failed';
      showToast(errorMsg, 'error', { title: 'Error' });
      setNewFiles(prev =>
        prev.map(f =>
          f.id === tempId ? { ...f, isUploading: false, error: errorMsg } : f
        )
      );
    }
  };

  // 🔥 处理编辑模式下粘贴图片
  const handleEditPaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      // 🔥 同时支持 MIME 类型和文件扩展名检查（与 ChatInput 保持一致）
      const allowedMimes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/webp',
      ];
      const allowedExts = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
      let hasInvalid = false;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind !== 'file') continue;
        const file = item.getAsFile();
        if (!file) continue;

        // 🔥 检查 MIME 类型和文件扩展名
        const mime = (file.type || '').toLowerCase();
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        const isAllowed =
          (mime && allowedMimes.includes(mime)) ||
          (ext && allowedExts.includes(ext));

        // 如果不是图片类型，跳过
        if (!mime.startsWith('image/') && !ext) continue;

        if (!isAllowed) {
          hasInvalid = true;
          continue;
        }

        e.preventDefault();
        uploadNewFile(file);
      }

      if (hasInvalid) {
        showToast('Paste jpg/jpeg/png/gif/webp images only', 'error', {
          title: 'Images only',
        });
      }
    },
    [uploadNewFile]
  );

  const [isUserTextExpanded, setIsUserTextExpanded] = useState(false);
  const [userTextPreviewFile, setUserTextPreviewFile] = useState<{
    url: string;
    filename: string;
  } | null>(null);
  // 🔥 图片预览状态：支持缩放和上一张/下一张导航
  const [previewImage, setPreviewImage] = useState<{
    images: LightboxImage[];
    currentIndex: number;
  } | null>(null);
  // 为 otherBlocks copy 按钮的独立状态
  const [otherBlocksCopied, setOtherBlocksCopied] = useState(false);
  // 🔥 消息取消相关状态
  const [isCanceling, setIsCanceling] = useState(false);
  // 获取用户信息
  const userInfo = useAuthStore(state => state.userInfo);
  // 获取用户名首字符
  const getUserInitial = () => {
    if (userInfo?.name) {
      return userInfo.name.charAt(0).toUpperCase();
    }
    if (userInfo?.username) {
      return userInfo.username.charAt(0).toUpperCase();
    }
    if (userInfo?.email) {
      return userInfo.email.charAt(0).toUpperCase();
    }
    return 'U';
  };
  // console.log('MessageBubble render:', message);
  // const [isHovered, setIsHovered] = useState(false);
  // Use custom hooks
  const { handleCopyClick, handleRetry, handleDelete, copied } =
    useMessageActions(message, onRetry, onDelete);
  const {
    isThinkingCollapsed,
    isToolCollapsed,
    toggleThinkingCollapse,
    toggleToolCollapse,
  } = useThinkingCollapse(message);

  const activeChoiceIndexForMessage = message.activeChoice ?? 0;
  // 优先使用 message 级别的 tokens（后端在 message_end 时返回）
  // 只有当 message 级别没有时才回退到 choice 级别
  const resolvedInputTokens =
    message.inputTokens ??
    message.choices?.[activeChoiceIndexForMessage]?.inputTokens;
  const resolvedCompletionTokens =
    message.tokens ?? message.choices?.[activeChoiceIndexForMessage]?.tokens;
  const hasAnyTokenValue =
    typeof resolvedInputTokens === 'number' ||
    typeof resolvedCompletionTokens === 'number';
  const resolvedTotalTokens =
    (typeof resolvedInputTokens === 'number' ? resolvedInputTokens : 0) +
    (typeof resolvedCompletionTokens === 'number'
      ? resolvedCompletionTokens
      : 0);
  const USER_TEXT_PREVIEW_LIMIT = 500;
  const USER_TEXT_PLACEHOLDER_LIMIT = 1000;

  // 🔥 清理文本中所有 *replay* 标签的辅助函数
  const stripAllReplayTags = (text: string): string => {
    // 使用 split + join 确保完全移除，避免正则问题
    return text.split('*replay*').join('').trim();
  };

  // 🔥 解析 *replay* 内容：格式为 *replay*用户名:内容*replay*
  // 使用最后一个 *replay* 作为外层闭合标记（处理嵌套情况）
  const parseReplayContent = (
    text: string
  ): {
    replyUser: string;
    replyContent: string;
    mainContent: string;
  } | null => {
    const TAG = '*replay*';
    if (!text.startsWith(TAG)) {
      return null;
    }

    // 找到所有 *replay* 标签的位置
    const positions: number[] = [];
    let idx = 0;
    while ((idx = text.indexOf(TAG, idx)) !== -1) {
      positions.push(idx);
      idx += TAG.length;
    }

    // 至少需要 2 个标签（开始 + 结束）
    if (positions.length < 2) return null;

    // 找到用户名（第一个 *replay* 和第一个 : 之间）
    const firstColon = text.indexOf(':', TAG.length);
    if (firstColon === -1) return null;

    const replyUser = text.substring(TAG.length, firstColon);

    // 🔥 关键修复：最后一个 *replay* 是外层闭合标记
    const outerCloseIndex = positions[positions.length - 1];

    // 回复内容：在第一个 : 和最后一个 *replay* 之间
    let replyContent = text.substring(firstColon + 1, outerCloseIndex);

    // 主消息内容：在最后一个 *replay* 之后的所有内容
    let mainContent = text.substring(outerCloseIndex + TAG.length);
    if (mainContent.startsWith('\n')) mainContent = mainContent.substring(1);

    // 🔥 彻底清理：移除所有残留的 *replay* 标签
    replyContent = stripAllReplayTags(replyContent);
    mainContent = stripAllReplayTags(mainContent);

    // 🔥 循环清理嵌套的 "用户名:" 前缀
    let cleaned = replyContent;
    let maxIterations = 5;
    while (maxIterations-- > 0) {
      const colonPos = cleaned.indexOf(':');
      if (colonPos === -1 || colonPos > 50) break;
      const beforeColon = cleaned.substring(0, colonPos);
      if (!beforeColon.includes('\n') && beforeColon.trim().length > 0) {
        cleaned = cleaned.substring(colonPos + 1).trim();
      } else {
        break;
      }
    }
    replyContent = cleaned;

    return {
      replyUser,
      replyContent,
      mainContent,
    };
  };

  const userText = useMemo(() => {
    if (!isUser) return '';
    const blocks = message.choices?.[activeChoiceIndexForMessage]?.blocks || [];
    const textBlocks = blocks.filter(
      (block: any) => block.type !== 'file' && block.type !== 'hash-code'
    );

    return textBlocks
      .map((block: any) =>
        typeof block.content === 'string' ? block.content : ''
      )
      .join('\n')
      .trim();
  }, [activeChoiceIndexForMessage, isUser, message.choices]);

  // 🔥 解析用户消息中的回复内容
  const parsedReply = useMemo(() => {
    if (!isUser || !userText) return null;
    return parseReplayContent(userText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUser, userText]);

  const userTextLength = userText.length;

  useEffect(() => {
    setIsUserTextExpanded(false);
  }, [message.id, activeChoiceIndexForMessage]);

  useEffect(() => {
    return () => {
      if (userTextPreviewFile?.url) {
        URL.revokeObjectURL(userTextPreviewFile.url);
      }
    };
  }, [userTextPreviewFile]);

  const openUserTextPreview = () => {
    if (!userText) return;
    const blob = new Blob([userText], {
      type: 'text/plain;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    setUserTextPreviewFile(prev => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return {
        url,
        filename: `user-message-${message.id}.txt`,
      };
    });
  };

  // 🔥 渲染回复卡片组件
  const ReplyCard = ({
    replyUser,
    replyContent,
  }: {
    replyUser: string;
    replyContent: string;
  }) => (
    <div className="flex items-start gap-2 bg-gray-100 border-l-4 border-blue-300 rounded-r-lg px-2 py-1.5 mb-2 max-w-full">
      <Reply size={14} className="text-blue-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-blue-600 mb-0.5">
          {replyUser}
        </div>
        <div className="text-xs text-gray-600 line-clamp-2">{replyContent}</div>
      </div>
    </div>
  );

  // 🔥 高亮渲染 [skill:XXX] 标签为浅蓝色
  const renderSkillHighlightedText = (text: string) => {
    const skillPattern = /(\[skill:[^\]]+\])/gi;
    const parts = text.split(skillPattern);
    if (parts.length === 1) return text; // no skill tags
    return parts.map((part, i) =>
      skillPattern.test(part) ? (
        <span
          key={i}
          className="inline-block px-1.5 py-0.5 text-sm font-medium text-blue-700 bg-blue-100 rounded"
        >
          {part}
        </span>
      ) : (
        <span key={i}>{part}</span>
      )
    );
  };

  const renderUserText = () => {
    if (!userText) return null;

    // 🔥 如果有回复内容，显示回复卡片 + 主消息
    if (parsedReply) {
      return (
        <div className="space-y-1">
          <ReplyCard
            replyUser={parsedReply.replyUser}
            replyContent={parsedReply.replyContent}
          />
          {parsedReply.mainContent && (
            <div className="whitespace-pre-wrap text-[15px] leading-relaxed text-gray-800">
              {/* 🔥 群聊：如果有 client.id 且是数字或 numeric string，显示 @Agent 标签 */}
              {isGroupChat &&
                message.client?.id &&
                (typeof message.client.id === 'number' ||
                  !isNaN(Number(message.client.id))) && (
                  <span className="inline-block px-1.5 py-0.5 text-xs font-medium text-gray-500 bg-gray-200 rounded mr-1 mb-1">
                    @Agent
                  </span>
                )}
              {renderSkillHighlightedText(parsedReply.mainContent)}
            </div>
          )}
        </div>
      );
    }

    if (userTextLength > USER_TEXT_PLACEHOLDER_LIMIT) {
      const snippet = userText.slice(0, 200).trimEnd();
      return (
        <button
          type="button"
          onClick={openUserTextPreview}
          className="rounded-lg border border-gray-300 bg-white/70 px-3 py-2 text-left max-w-[220px] hover:bg-white transition-colors"
          title="Click to view full text"
        >
          <div className="text-[10px] leading-snug text-gray-600 line-clamp-4 whitespace-pre-wrap">
            {isGroupChat &&
              message.client?.id &&
              (typeof message.client.id === 'number' ||
                !isNaN(Number(message.client.id))) && (
                <span className="inline-block px-1.5 py-0.5 text-xs font-medium text-gray-500 bg-gray-200 rounded mr-1 mb-1">
                  @Agent
                </span>
              )}
            {snippet ? renderSkillHighlightedText(snippet) : ' '}
          </div>
          <div className="mt-2 inline-flex items-center rounded border border-gray-300 bg-white px-2 py-0.5 text-[10px] font-medium text-gray-700">
            PASTED
          </div>
        </button>
      );
    }

    if (userTextLength > USER_TEXT_PREVIEW_LIMIT) {
      const preview = isUserTextExpanded
        ? userText
        : userText.slice(0, USER_TEXT_PREVIEW_LIMIT);

      return (
        <div className="space-y-2">
          <div className="relative">
            <div className="whitespace-pre-wrap text-[15px] leading-relaxed text-gray-800">
              {isGroupChat &&
                message.client?.id &&
                (typeof message.client.id === 'number' ||
                  !isNaN(Number(message.client.id))) && (
                  <span className="inline-block px-1.5 py-0.5 text-xs font-medium text-gray-500 bg-gray-200 rounded mr-1 mb-1">
                    @Agent
                  </span>
                )}
              {renderSkillHighlightedText(preview)}
            </div>
            {!isUserTextExpanded && (
              // <div className="absolute bottom-0 left-0 right-0 h-36 bg-gradient-to-t from-[#f0eee6] to-transparent pointer-events-none" />
              <div className="absolute bottom-0 left-0 right-0 h-36 bg-gradient-to-t from-[#f0eee6] to-transparent pointer-events-none"></div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setIsUserTextExpanded(!isUserTextExpanded)}
            className="inline-flex items-center justify-center rounded-md px-4 py-1.5 text-xs text-gray-600 hover:bg-white transition-colors"
          >
            {isUserTextExpanded ? t('common.showLess') : t('common.showMore')}
          </button>
        </div>
      );
    }

    return (
      <div className="whitespace-pre-wrap text-[15px] leading-relaxed text-gray-800">
        {/* 🔥 群聊：如果有 client.id 且是数字或 numeric string，显示 @Agent 标签 */}
        {isGroupChat &&
          message.client?.id &&
          (typeof message.client.id === 'number' ||
            !isNaN(Number(message.client.id))) && (
            <span className="inline-block px-1.5 py-0.5 text-xs font-medium text-gray-500 bg-gray-200 rounded mr-1 mb-1">
              @Agent
            </span>
          )}
        {renderSkillHighlightedText(userText)}
      </div>
    );
  };
  // 版本切换处理函数
  // 版本切换器控制的是父层的版本选择（choices.length）
  const handlePreviousChoice = () => {
    const currentChoice = message.activeChoice ?? 0;
    // Previous = 数字减小 = 索引减小
    if (currentChoice > 0 && onChangeChoice) {
      onChangeChoice(String(message.id), currentChoice - 1);
    }
  };
  const handleNextChoice = () => {
    const currentChoice = message.activeChoice ?? 0;
    const totalChoices = message.choices.length || 1; // 使用 choices.length 而不是 multiVersion
    // Next = 数字增大 = 索引增大
    if (currentChoice < totalChoices - 1 && onChangeChoice) {
      onChangeChoice(String(message.id), currentChoice + 1);
    }
  };
  // 编辑相关处理函数
  const handleEditClick = () => {
    const activeIndex = message.activeChoice ?? 0;
    const blocks = message.choices[activeIndex]?.blocks || [];
    // 只提取文本块的内容，跳过文件和hash-code
    const textContent = blocks
      .filter(block => block.type === 'text')
      .map(block => block.content)
      .join('\n')
      .trim();
    setEditedContent(textContent || '');
    // 🔥 提取文件块的 fileId，初始化附件编辑状态
    // 🔥 确保转换为字符串，与 useState<string[]> 类型和后续比较逻辑保持一致
    const fileIds = blocks
      .filter((block: any) => block.type === 'file')
      .map((block: any) => block.fileId || block.content || block.id)
      .filter(Boolean)
      .map((id: any) => String(id));
    setEditedFileIds(fileIds);
    setNewFiles([]);
    setIsEditing(true);
  };
  const handleEditSave = () => {
    if (
      onEditSave &&
      (editedContent.trim() || editedFileIds.length > 0 || newFiles.length > 0)
    ) {
      // 🔥 收集已上传成功的 fileIds（过滤掉正在上传的或失败的）
      // 🔥 确保转换为字符串，因为服务器可能返回数字
      const uploadedNewFileIds = newFiles
        .filter(f => f.fileId && !f.isUploading && !f.error)
        .map(f => String(f.fileId));
      // 🔥 合并现有和新上传的 fileIds，并确保都是字符串
      const allFileIds = [...editedFileIds, ...uploadedNewFileIds]
        .filter(Boolean)
        .map(id => String(id));

      onEditSave(message, editedContent.trim(), allFileIds);
      setIsEditing(false);
      setEditedFileIds([]);
      // 🔥 清理预览 URL
      newFiles.forEach(f => URL.revokeObjectURL(f.previewUrl));
      setNewFiles([]);
    }
  };
  const handleEditCancel = () => {
    setIsEditing(false);
    setEditedContent('');
    setEditedFileIds([]);
    setNewFiles([]);
  };

  // 🔥 群聊：回复消息处理
  const handleReply = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onReply) {
      onReply(message);
    }
  };

  // 🔥 消息取消处理
  const handleCancel = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isCanceling || message.messageStatus === 'canceled') {
      return;
    }
    // waiting 状态直接取消，不需要 loading 状态（因为消息会被移除）
    // pending/ongoing 状态需要调用 API，显示 loading 状态
    if (message.messageStatus !== 'waiting') {
      setIsCanceling(true);
    }
    if (onCancel) {
      onCancel();
    }
  };

  // 复制最后一个 otherBlocks 的内容
  const handleCopyOtherBlocks = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const activeIndex = message.activeChoice ?? 0;
      const blocks = message.choices[activeIndex]?.blocks || [];

      // 找到最后一个 'text' 类型的 block
      let lastOtherContent = '';
      for (let i = blocks.length - 1; i >= 0; i--) {
        const block = blocks[i];
        if (block.type === 'text') {
          lastOtherContent = block.content;
          break;
        }
      }

      if (lastOtherContent) {
        await navigator.clipboard.writeText(lastOtherContent);
        // 更新状态，2秒后恢复
        setOtherBlocksCopied(true);
        setTimeout(() => setOtherBlocksCopied(false), 2000);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // 反馈处理函数
  const handleFeedbackSubmit = async (
    score: number,
    feedback: string,
    issueTypes: string
  ) => {
    if (!conversationId || !message.backendId) {
      toast({
        description:
          'Unable to submit feedback: missing conversation or message ID',
        variant: 'destructive',
      });
      return;
    }

    try {
      await submitMessageFeedback(
        conversationId,
        message.backendId,
        score,
        feedback,
        issueTypes
      );
      toast({
        description: 'Feedback submitted successfully',
        className: 'border-green-500 bg-green-50 text-green-900',
      });
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      toast({
        description: 'Failed to submit feedback',
        variant: 'destructive',
      });
    }
  };

  /**
   * Render block-based content
   */
  const renderBlockContent = () => {
    if (!message.choices || message.choices.length === 0) {
      // Backward compatibility: if no blocks, use original content rendering
      return <div className="message-content">No content found here.</div>;
    }
    if (isUser) {
      // 使用 activeChoice 来获取当前选中的版本内容
      const blocks = message.choices[activeChoiceIndexForMessage]?.blocks || [];

      // 🔥 群聊：判断是否是其他用户的消息
      // 1. WebSocket 消息：isOwnMessage 明确设置为 false
      // 2. 历史消息：比较 user.id 和当前用户 id
      const currentUserId = userInfo?.id;
      const messageUserId = otherUser?.id;
      const isOtherUserMessage =
        isOwnMessage === false || // WebSocket 明确标记
        (otherUser &&
          currentUserId &&
          String(messageUserId) !== String(currentUserId)); // 历史消息比较

      // 🔥 群聊：其他用户的消息渲染在左边
      if (isOtherUserMessage && otherUser) {
        // 获取其他用户的首字母
        const getOtherUserInitial = () => {
          if (otherUser.name) {
            return otherUser.name.charAt(0).toUpperCase();
          }
          if (otherUser.email) {
            return otherUser.email.charAt(0).toUpperCase();
          }
          return 'U';
        };

        // 获取图片块 - 🔥 添加防御性过滤，排除无效的文件 ID
        const imageBlocks = blocks.filter(block => {
          if (block.type !== 'file') return false;
          const fileId = (block as any).fileId || block.content || block.id;
          // 排除 undefined, null, 空字符串, 和字符串 "undefined"
          return fileId && fileId !== 'undefined';
        });
        // 确保有内容可显示（文字或图片）
        const displayText =
          userText || (imageBlocks.length === 0 ? '[消息]' : '');

        return (
          <div className="space-y-1 group flex flex-col items-start">
            {/* 用户名显示在气泡上方 - 灰色 */}
            <div className="text-xs text-gray-800 font-medium ml-1">
              {otherUser.name || otherUser.email || 'User'}
            </div>
            {/* 图片部分 - 如果有图片 */}
            {imageBlocks.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {imageBlocks.map((block, index) => (
                  <div
                    key={`other-user-image-${index}`}
                    className="rounded-lg overflow-hidden border border-gray-400"
                    style={{ width: '120px', height: '120px' }}
                  >
                    {((block as any).fileId || block.content || block.id) && (
                      <img
                        src={`${window.location.origin}/api/messages/files/${encodeURIComponent((block as any).fileId || block.content || block.id)}?Authorization=${localStorage.getItem('user-token')}`}
                        alt="Message image"
                        className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => {
                          // 🔥 收集当前消息中的所有图片，支持消息内导航
                          const allImages: LightboxImage[] = imageBlocks.map(
                            b => {
                              const fId =
                                (b as any).fileId || b.content || b.id;
                              return {
                                src: `${window.location.origin}/api/messages/files/${encodeURIComponent(fId)}?Authorization=${localStorage.getItem('user-token')}`,
                                alt: 'Message image',
                              };
                            }
                          );
                          setPreviewImage({
                            images: allImages,
                            currentIndex: index,
                          });
                        }}
                        onLoad={e => {
                          (e.target as HTMLImageElement).style.display = '';
                        }}
                        onError={e => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
            {/* 消息气泡：样式与 self_message 完全一致，只是左对齐 */}
            {(displayText || imageBlocks.length === 0) && (
              <div className="inline-flex gap-3 px-2 py-1 items-start max-w-[calc(100%-100px)] bg-[#f0eee6] rounded-xl">
                {/* 头像 - 蓝绿色，区分其他用户 */}
                <div className="w-7 h-7 rounded-full bg-yellow-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 mt-2.5">
                  {getOtherUserInitial()}
                </div>
                <div className="flex-1 pr-2 py-2.5 rounded-2xl min-w-0">
                  {/* 🔥 如果有回复内容，显示回复卡片 */}
                  {parsedReply ? (
                    <div className="space-y-1">
                      <ReplyCard
                        replyUser={parsedReply.replyUser}
                        replyContent={parsedReply.replyContent}
                      />
                      {parsedReply.mainContent && (
                        <div className="whitespace-pre-wrap text-[15px] leading-relaxed text-gray-800">
                          {parsedReply.mainContent}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="whitespace-pre-wrap text-[15px] leading-relaxed text-gray-800">
                      {/* 🔥 群聊：如果有 client.id 且是数字，显示 @Agent 标签 */}
                      {isGroupChat &&
                        message.client?.id &&
                        typeof message.client.id === 'number' && (
                          <span className="inline-block px-1.5 py-0.5 text-xs font-medium text-gray-500 bg-gray-200 rounded mr-1 mb-1">
                            {/* @{message.client.name || 'Agent'} */}
                            @Agent
                          </span>
                        )}
                      {displayText}
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* 🔥 群聊：其他用户消息也显示操作按钮 - 鼠标悬停时显示 */}
            <div className="flex justify-start opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-full">
              <MessageActions
                isStreaming={isAnyStreaming}
                onCopy={handleCopyClick}
                copied={copied}
                readOnly={readOnly}
                isUser={true}
                isGroupChat={isGroupChat}
                onReply={onReply ? handleReply : undefined}
              />
            </div>
          </div>
        );
      }

      // 编辑模式 - 编辑文本和附件
      if (isEditing) {
        return (
          <div className="space-y-2 min-w-[460px]">
            <textarea
              value={editedContent}
              onChange={e => setEditedContent(e.target.value)}
              onPaste={handleEditPaste}
              className="w-full min-h-[100px] px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
              autoFocus
              onFocus={e => {
                const len = e.target.value.length;
                e.target.setSelectionRange(len, len);
                e.target.scrollTop = e.target.scrollHeight;
              }}
            />

            {/* 🔥 附件编辑区域 */}
            <div className="space-y-2">
              {/* 🔥 所有附件（现有 + 新添加）在同一个 flex 容器中 */}
              {(editedFileIds.length > 0 || newFiles.length > 0) && (
                <div className="flex flex-wrap gap-2">
                  {/* 现有附件 */}
                  {editedFileIds.map((fileId, index) => (
                    <div key={`existing-${fileId}`} className="relative group">
                      <img
                        src={`${window.location.origin}/api/messages/files/${encodeURIComponent(fileId)}?Authorization=${localStorage.getItem('user-token')}`}
                        alt={`Attachment ${index + 1}`}
                        className="w-16 h-16 object-cover rounded border border-gray-300"
                      />
                      <button
                        onClick={() =>
                          setEditedFileIds(prev =>
                            prev.filter(id => id !== fileId)
                          )
                        }
                        className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove attachment"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  {/* 新添加的文件（带上传状态） */}
                  {newFiles.map(fileUpload => (
                    <div key={fileUpload.id} className="relative group">
                      {fileUpload.file.type.startsWith('image/') ? (
                        <div className="relative">
                          <img
                            src={fileUpload.previewUrl}
                            alt={fileUpload.file.name}
                            className="w-16 h-16 object-cover rounded border border-blue-300"
                          />
                          {/* 上传中状态 */}
                          {fileUpload.isUploading && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded">
                              <Loader2
                                size={16}
                                className="animate-spin text-white"
                              />
                            </div>
                          )}
                          {/* 上传失败状态 */}
                          {fileUpload.error && (
                            <div className="absolute inset-0 bg-red-500/50 flex items-center justify-center rounded">
                              <span className="text-white text-xs font-medium">
                                Error!
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded border border-blue-300 text-xs text-gray-500 truncate p-1">
                          {fileUpload.file.name}
                        </div>
                      )}
                      <button
                        onClick={() => {
                          // 释放预览 URL
                          URL.revokeObjectURL(fileUpload.previewUrl);
                          setNewFiles(prev =>
                            prev.filter(f => f.id !== fileUpload.id)
                          );
                        }}
                        className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove attachment"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* 添加附件按钮 */}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
              >
                <span>+ Add Attachment</span>
              </button>

              {/* 隐藏的文件输入 */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={e => {
                  const files = Array.from(e.target.files || []);
                  // 🔥 选择文件时立即上传
                  files.forEach(file => uploadNewFile(file));
                  e.target.value = ''; // Reset input
                }}
                className="hidden"
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleEditSave}
                disabled={newFiles.some(f => f.isUploading || f.error)}
                className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs text-white rounded-lg transition-colors ${
                  newFiles.some(f => f.isUploading || f.error)
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                <Check size={14} />
                <span>Save & Retry</span>
              </button>
              <button
                onClick={handleEditCancel}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X size={14} />
                <span>Cancel</span>
              </button>
            </div>
          </div>
        );
      }
      // 正常显示模式 - 分离图片和文字
      // 🔥 添加防御性过滤，排除无效的文件 ID
      const imageBlocks = blocks.filter(block => {
        if (block.type !== 'file') return false;
        const fileId = (block as any).fileId || block.content || block.id;
        // 排除 undefined, null, 空字符串, 和字符串 "undefined"
        return fileId && fileId !== 'undefined';
      });
      // 🔥 修复：确保有内容可显示（文字或图片），否则显示占位符
      const selfDisplayText =
        userText || (imageBlocks.length === 0 ? '[消息]' : '');

      return (
        <div className="space-y-2 group flex flex-col items-end">
          {/* 图片部分 - 如果有图片 */}
          {imageBlocks.length > 0 && (
            <div className="flex gap-2 flex-wrap justify-end ml-auto max-w-[80%]">
              {imageBlocks.map((block, index) => (
                <div
                  key={`user-image-${index}`}
                  className="rounded-lg overflow-hidden border border-gray-400 "
                  style={{ width: '120px', height: '120px' }}
                >
                  {((block as any).fileId || block.content || block.id) && (
                    <img
                      src={`${window.location.origin}/api/messages/files/${encodeURIComponent((block as any).fileId || block.content || block.id)}?Authorization=${localStorage.getItem('user-token')}`}
                      alt="Message image"
                      className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => {
                        // 🔥 构建所有图片列表，支持上一张/下一张导航
                        const allImages: LightboxImage[] = imageBlocks.map(
                          b => {
                            const fId = (b as any).fileId || b.content || b.id;
                            return {
                              src: `${window.location.origin}/api/messages/files/${encodeURIComponent(fId)}?Authorization=${localStorage.getItem('user-token')}`,
                              alt: 'Message image',
                            };
                          }
                        );
                        setPreviewImage({
                          images: allImages,
                          currentIndex: index,
                        });
                      }}
                      onLoad={e => {
                        (e.target as HTMLImageElement).style.display = '';
                      }}
                      onError={e => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* 文字部分 - 如果有文字或没有图片（确保至少显示一个气泡） */}
          {(selfDisplayText || imageBlocks.length === 0) && (
            <div className="inline-flex gap-3 px-2 py-1 items-start max-w-[calc(100%-100px)] bg-[#f0eee6] rounded-xl">
              <div className="w-7 h-7 rounded-full bg-gray-700 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 mt-2.5">
                {getUserInitial()}
              </div>
              <div className="flex-1 pr-2 py-2.5 rounded-2xl min-w-0">
                {userTextLength > 0 ? (
                  renderUserText()
                ) : (
                  <div className="whitespace-pre-wrap text-[15px] leading-relaxed text-gray-500">
                    {/* 🔥 群聊：如果有 client.id 且是数字或 numeric string，显示 @Agent 标签 */}
                    {isGroupChat &&
                      message.client?.id &&
                      (typeof message.client.id === 'number' ||
                        !isNaN(Number(message.client.id))) && (
                        <span className="inline-block px-1.5 py-0.5 text-xs font-medium text-gray-500 bg-gray-200 rounded mr-1 mb-1">
                          @Agent
                        </span>
                      )}
                    {selfDisplayText}
                  </div>
                )}
              </div>
              {/* 🔥 状态标签放在 user 消息气泡内部，消息内容后面（只在 user 节点显示） */}
              {message.role === 'user' && (
                <div className="flex items-center gap-2 self-center flex-shrink-0 animate-in fade-in slide-in-from-right-2 duration-300">
                  {/* {(message as any).messageStatus === 'waiting' && (
                    <div className="inline-flex items-center gap-2">
                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-300 rounded-full animate-pulse shadow-sm">
                        <Clock size={12} />
                        <span>waiting</span>
                      </div>
                      {onCancel && (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 border-red-300  bg-red-100 hover:text-red-400 border rounded-full transition-all duration-200"
                          title="Cancel"
                          onClick={onCancel}
                        >
                          <Square size={10} />
                          <span>cancel</span>
                        </button>
                      )}
                    </div>
                  )} */}
                  {/* 🔥 暂时隐藏 pending 和 ongoing 状态标签，只显示 waiting */}
                  {/* {(message as any).messageStatus === 'pending' && (
                    <div className="inline-flex items-center gap-2">
                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full shadow-sm">
                        <Loader2 size={12} className="animate-spin" />
                        <span>pending</span>
                      </div>
                      {onCancel && (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 border-red-300  bg-red-100 hover:text-red-400 border rounded-full transition-all duration-200"
                          title="Cancel"
                          onClick={onCancel}
                        >
                          <Square size={10} />
                          <span>cancel</span>
                        </button>
                      )}
                    </div>
                  )}
                  {(message as any).messageStatus === 'ongoing' && (
                    <div className="inline-flex items-center gap-2">
                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full shadow-sm">
                        <Loader2 size={12} className="animate-spin" />
                        <span>ongoing</span>
                      </div>
                      {onCancel && (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 border-red-300 bg-red-100 hover:text-red-400 border rounded-full transition-all duration-200"
                          title="Cancel"
                          onClick={onCancel}
                        >
                          <Square size={10} />
                          <span>cancel</span>
                        </button>
                      )}
                    </div>
                  )} */}
                  {/* 🔥 新增：canceled 状态 - 消息已取消 */}
                  {((message as any).messageStatus === 'canceled' ||
                    message.isCanceled) && (
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-500 bg-gray-100 border border-gray-200 rounded-full shadow-sm">
                      <span>canceled</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {/* User 消息也显示操作按钮和版本切换器 - 鼠标悬停时显示 */}
          <div className="flex justify-end opacity-0 group-hover:opacity-100 transition-opacity duration-200 self-start w-full">
            <MessageActions
              isStreaming={isAnyStreaming}
              onCopy={handleCopyClick}
              copied={copied}
              onRetry={onRetry ? handleRetry : undefined}
              onDelete={onDelete ? handleDelete : undefined}
              onEditSave={onEditSave ? handleEditClick : undefined}
              readOnly={readOnly}
              multiVersion={message.choices.length}
              activeChoice={message.activeChoice ?? 0}
              isUser={isUser}
              onPreviousChoice={
                message.choices.length && message.choices.length > 1
                  ? handlePreviousChoice
                  : undefined
              }
              onNextChoice={
                message.choices.length && message.choices.length > 1
                  ? handleNextChoice
                  : undefined
              }
              conversationId={conversationId}
              messageId={message.backendId}
              onFeedbackSubmit={handleFeedbackSubmit}
              initialScore={message.score}
              createdAt={message.timestamp}
              isGroupChat={isGroupChat}
              onReply={onReply ? handleReply : undefined}
            />
          </div>
        </div>
      );
    }
    const activeChoice = message.activeChoice ?? 0;
    const blocks = message.choices?.[activeChoice]?.blocks || [];

    // 按顺序分组：连续的相同类型合并为一组
    const groupedBlocks: { type: 'think' | 'other'; blocks: any[] }[] = [];
    let currentGroup: { type: 'think' | 'other'; blocks: any[] } | null = null;

    blocks?.forEach(block => {
      const isThinkBlock =
        block.type === 'think' ||
        block.type === 'call' ||
        block.type === 'result';
      const blockGroupType = isThinkBlock ? 'think' : 'other';

      if (!currentGroup || currentGroup.type !== blockGroupType) {
        // 创建新组
        currentGroup = { type: blockGroupType, blocks: [block] };
        groupedBlocks.push(currentGroup);
      } else {
        // 添加到当前组
        currentGroup.blocks.push(block);
      }
    });

    return (
      <div className="message-content break-normal mb-2">
        {/* 按顺序渲染分组的 blocks */}
        {groupedBlocks.map((group, groupIndex) => (
          <div key={`group-${groupIndex}`}>
            {group.type === 'think' ? (
              // 渲染 thinking section
              <ThinkingSection
                thinkBlocks={group.blocks}
                isThinkingCollapsed={
                  isThinkingCollapsed.get(groupIndex) ?? true
                }
                isToolCollapsed={isToolCollapsed}
                onToggleThinking={toggleThinkingCollapse}
                onToggleTool={toggleToolCollapse}
                groupIndex={groupIndex}
              />
            ) : (
              // 渲染 other blocks
              group.blocks.map((block, blockIndex) => (
                <div
                  key={`other-${groupIndex}-${blockIndex}`}
                  className={cn('block-content', `block-${block.type}`)}
                >
                  {block.type === 'file' ? (
                    // Render image for file type
                    <div className="my-2">
                      {(block.content || block.id || (block as any).name) && (
                        <img
                          src={`${window.location.origin}/api/messages/files/${encodeURIComponent(block.content || block.id || (block as any).name)}/metadata?token=${localStorage.getItem('user-token')}`}
                          alt="Message image"
                          className="max-w-full max-h-96 rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() =>
                            setPreviewImage({
                              images: [
                                {
                                  src: `${window.location.origin}/api/messages/files/${encodeURIComponent(block.content || block.id || (block as any).name)}/metadata?token=${localStorage.getItem('user-token')}`,
                                  alt: 'Message image',
                                },
                              ],
                              currentIndex: 0,
                            })
                          }
                          onError={e => {
                            // Fallback if image fails to load
                            (e.target as HTMLImageElement).style.display =
                              'none';
                          }}
                        />
                      )}
                    </div>
                  ) : (
                    <div
                      className={cn(
                        block.type === 'text'
                          ? 'text-[15px] leading-relaxed'
                          : block.type === 'ref'
                            ? 'my-2'
                            : '',
                        block.type === 'hash-code' ? 'opacity-30 text-xs' : ''
                      )}
                    >
                      {block.type === 'hash-code' ? (
                        <div className="flex items-center gap-2 text-xs text-gray-500 justify-start mb-2">
                          <span className="truncate">{block.content}</span>
                          <span className="whitespace-nowrap">
                            tokens:{' '}
                            {hasAnyTokenValue ? resolvedTotalTokens : '-'}
                          </span>
                        </div>
                      ) : (
                        <MarkdownRenderer
                          content={
                            block.type === 'error'
                              ? `**Error:** ${block.content}`
                              : block.content
                          }
                          className="text-gray-800"
                          client={message.client}
                        />
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        ))}
        {/* 操作按钮 - 流式传输时隐藏 */}
        {!message.isStreaming && (
          <div className="flex justify-start">
            <MessageActions
              onCopy={handleCopyOtherBlocks}
              copied={otherBlocksCopied}
              onRetry={onRetry ? handleRetry : undefined}
              onDelete={onDelete ? handleDelete : undefined}
              readOnly={readOnly}
              isStreaming={isAnyStreaming}
              multiVersion={message.choices.length}
              activeChoice={message.activeChoice ?? 0}
              isUser={isUser}
              onPreviousChoice={
                message.choices.length ? handlePreviousChoice : undefined
              }
              onNextChoice={
                message.choices.length ? handleNextChoice : undefined
              }
              conversationId={conversationId}
              messageId={message.backendId}
              onFeedbackSubmit={handleFeedbackSubmit}
              initialScore={message.score}
              createdAt={message.timestamp}
              client={message.client}
              isGroupChat={isGroupChat}
              messageStatus={message.messageStatus}
              onCancel={onCancel ? handleCancel : undefined}
              isCanceling={isCanceling}
            />
          </div>
        )}
        {/* 🔥 修改：loading 动画在 isStreaming 时显示（包括 waiting 状态） */}
        {/* 🔥 Cancel 按钮已移至 user 节点的状态标签区域，assistant 节点不再显示 Cancel 按钮 */}
        {message.isStreaming && (
          <div className="flex items-center gap-2 mt-3">
            <div className="typing-indicator-container inline-block">
              <span className="typing-dot typing-dot-1"></span>
              <span className="typing-dot typing-dot-2"></span>
              <span className="typing-dot typing-dot-3"></span>
              <span className="typing-dot typing-dot-4"></span>
            </div>
          </div>
        )}
      </div>
    );
  };
  return (
    <div className="w-full py-0">
      {/* User text preview drawer */}
      {userTextPreviewFile && (
        <FilePreview
          fileUrl={userTextPreviewFile.url}
          filename={userTextPreviewFile.filename}
          mode="overlay"
          readOnly
          onClose={() => {
            if (userTextPreviewFile?.url)
              URL.revokeObjectURL(userTextPreviewFile.url);
            setUserTextPreviewFile(null);
          }}
        />
      )}
      {/* 🔥 图片预览 Lightbox — 支持缩放、上一张/下一张导航 */}
      {previewImage && (
        <ImageLightbox
          images={previewImage.images}
          currentIndex={previewImage.currentIndex}
          onClose={() => setPreviewImage(null)}
        />
      )}
      <div className="flex gap-0 ">
        <div
          className={cn(
            'rounded-lg relative w-full',
            isUser ? 'px-0 pb-2 pt-2' : 'px-0 pt-0 pb-2'
          )}
        >
          {renderBlockContent()}
        </div>
        {/* Sources */}
        {message.sources && message.sources.length > 0 && (
          <div className="mt-3 space-y-2 ml-10 ">
            {message.sources.map(source => (
              <SourceCard
                key={source.id}
                source={source}
                onClick={page => onSourceClick(source.id, page)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
